import * as React from "react";
import { useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Textarea,
  Button,
  Switch,
  Tag,
  Label,
  Text,
  Spinner,
  Toast,
  ToastTitle,
  ToastBody,
  ToastTrigger,
  useToastController,
} from "@fluentui/react-components";
import {
  DocumentText20Regular,
  Dismiss16Regular,
  ErrorCircle20Filled,
  Person20Regular,
} from "@fluentui/react-icons";
import { useSettings } from "../settings/useSettings";
import { useHostSurface } from "../host/useHostSurface";
import { sendSms, sendBulk, dryRunSms, getAccountInfo } from "../settings/lox24Api";
import { dialCodeForPhone } from "../settings/countries";
import { computeSegments } from "./segments";
import { normalizePhone } from "./phone";
import RecipientsInput from "./RecipientsInput";
import ContactsPickerDialog from "./ContactsPickerDialog";
import TemplatesDialog from "../templates/TemplatesDialog";
import { ScrollFade, useScrollFade } from "../../components/ScrollFade";
import { TOASTER_ID } from "../../toaster";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  panel: { display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0 },
  fields: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    overflowY: "auto",
    flexGrow: 1,
    minHeight: 0,
    padding: "12px",
  },
  senderBox: {
    display: "flex",
    alignItems: "center",
    minHeight: "32px",
    padding: "3px 6px",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  senderEmpty: { color: tokens.colorNeutralForeground3, paddingLeft: "2px" },
  fieldBlock: { display: "flex", flexDirection: "column", gap: "4px" },
  fieldHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  counter: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: "right",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "10px 12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  summary: {
    display: "flex",
    justifyContent: "space-between",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  sendButton: { width: "100%" },
  loading: { display: "flex", justifyContent: "center", padding: "24px" },
});

// Current UTC offset as "UTC+05:30". The scheduled time entered is local; we
// convert it to a UTC unix timestamp (delivery_at) at send, so this only clarifies
// to the user how their input is interpreted.
function localTimezoneLabel(): string {
  const mins = -new Date().getTimezoneOffset(); // minutes east of UTC
  const sign = mins >= 0 ? "+" : "-";
  const abs = Math.abs(mins);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

interface SendSmsPanelProps {
  /** Graph token from the signed-in session, used by the contacts picker. */
  graphToken?: string;
}

const SendSmsPanel: React.FC<SendSmsPanelProps> = ({ graphToken }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const mailMode = useHostSurface() === "mail";
  const { dispatchToast } = useToastController(TOASTER_ID);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [appendSignature, setAppendSignature] = useState(true);
  const [scheduled, setScheduled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [sending, setSending] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [balance, setBalance] = useState<{ amount: number; currency: string } | null>(null);
  const [estCost, setEstCost] = useState<number | null>(null);
  const [estLoading, setEstLoading] = useState(false);
  const { scrollRef, showFade, updateFade, scrollDown } = useScrollFade();

  const apiKey = settings?.apiKey;
  const senderId = settings?.senderId;
  const defaultCountry = settings?.defaultCountry;
  const smsType = settings?.smsType;
  const encoding = settings?.encoding;
  const signature = settings?.signature;

  useEffect(() => {
    if (!apiKey) return undefined;
    let cancelled = false;
    getAccountInfo(apiKey)
      .then((info) => {
        if (!cancelled && typeof info.balance_amount === "number") {
          setBalance({ amount: info.balance_amount, currency: info.currency ?? "" });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Estimated cost via /sms/dryrun on the first recipient (debounced).
  useEffect(() => {
    if (
      !apiKey ||
      !senderId ||
      !defaultCountry ||
      !smsType ||
      !encoding ||
      recipients.length === 0 ||
      !message.trim()
    ) {
      setEstCost(null);
      return undefined;
    }
    const phones = recipients.map((r) => normalizePhone(r, defaultCountry)).filter(Boolean);
    if (phones.length === 0) {
      setEstCost(null);
      return undefined;
    }
    const text = appendSignature && signature?.trim() ? `${message}\n${signature}` : message;
    let cancelled = false;
    setEstLoading(true);
    const timer = setTimeout(async () => {
      try {
        // The bulk dry-run doesn't price (returns 0). Price each country once with
        // the single dry-run (which returns a real price), then scale by count.
        const groups = new Map<string, string[]>();
        for (const phone of phones) {
          const key = dialCodeForPhone(phone) ?? phone;
          const arr = groups.get(key) ?? [];
          arr.push(phone);
          groups.set(key, arr);
        }
        const prices = await Promise.all(
          [...groups.values()].map(async (arr) => {
            const r = await dryRunSms(apiKey, {
              senderId,
              phone: arr[0],
              text,
              serviceCode: smsType,
              isUnicode: encoding === "unicode",
            });
            return (typeof r.price === "number" ? r.price : 0) * arr.length;
          })
        );
        if (!cancelled) setEstCost(prices.reduce((sum, p) => sum + p, 0));
      } catch {
        if (!cancelled) setEstCost(null);
      } finally {
        if (!cancelled) setEstLoading(false);
      }
    }, 800);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    apiKey,
    senderId,
    defaultCountry,
    smsType,
    encoding,
    signature,
    recipients,
    message,
    appendSignature,
  ]);

  // Re-evaluate the scroll fade when the form's height can change (recipient
  // chips wrap, signature/schedule fields appear).
  useEffect(updateFade, [recipients, scheduled, signature, settings, updateFade]);

  const notify = (title: string, body: string, intent: "success" | "error" | "warning") =>
    dispatchToast(
      <Toast>
        <ToastTitle
          media={
            intent === "error" ? (
              <ErrorCircle20Filled style={{ color: tokens.colorStatusDangerForeground1 }} />
            ) : undefined
          }
          action={
            <ToastTrigger>
              <Button
                appearance="transparent"
                size="small"
                icon={<Dismiss16Regular />}
                aria-label={t("common.close")}
              />
            </ToastTrigger>
          }
        >
          {title}
        </ToastTitle>
        {body && <ToastBody>{body}</ToastBody>}
      </Toast>,
      { intent }
    );

  if (!settings) {
    return (
      <div className={styles.loading}>
        <Spinner size="small" label={t("common.loading")} />
      </div>
    );
  }

  const hasSignature = settings.signature.trim().length > 0;
  const outgoingText =
    appendSignature && hasSignature ? `${message}\n${settings.signature}` : message;
  const seg = computeSegments(outgoingText, settings.encoding);

  const insertTemplate = (text: string) => setMessage((m) => (m ? `${m}${text}` : text));

  const onSend = async () => {
    if (!settings.senderId.trim()) {
      notify(t("send.error.senderMissing"), t("send.error.senderMissingBody"), "error");
      return;
    }
    const numbers = recipients
      .map((r) => normalizePhone(r, settings.defaultCountry))
      .filter(Boolean);
    if (numbers.length === 0) {
      notify(t("send.error.noRecipient"), t("send.error.noRecipientBody"), "error");
      return;
    }
    if (!message.trim()) {
      notify(t("send.error.messageEmpty"), t("send.error.messageEmptyBody"), "error");
      return;
    }

    let deliveryAt: number | undefined;
    if (scheduled) {
      const ts = Math.floor(new Date(scheduleAt).getTime() / 1000);
      if (!scheduleAt || Number.isNaN(ts) || ts * 1000 <= Date.now()) {
        notify(t("send.error.invalidTime"), t("send.error.invalidTimeBody"), "error");
        return;
      }
      deliveryAt = ts;
    }

    setSending(true);
    try {
      const common = {
        senderId: settings.senderId,
        text: outgoingText,
        serviceCode: settings.smsType,
        isUnicode: settings.encoding === "unicode",
        deliveryAt,
      };
      let count = numbers.length;
      if (numbers.length === 1) {
        await sendSms(settings.apiKey, { ...common, phone: numbers[0] });
      } else {
        const r = await sendBulk(settings.apiKey, { ...common, phones: numbers });
        count = r.total ?? numbers.length;
      }
      // Four whole sentences, not a stem plus a verb: no two languages inflect
      // "was sent" / "were sent" alike, or place the count the same way.
      notify(
        scheduled ? t("send.result.scheduled") : t("send.result.sent"),
        scheduled
          ? t("send.result.scheduledBody", { count })
          : t("send.result.sentBody", { count }),
        "success"
      );
      setMessage("");
      setRecipients([]);
    } catch (e) {
      notify(t("send.error.sendFailed"), (e as Error).message, "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.fields} ref={scrollRef} onScroll={updateFade}>
        <Field label={t("send.from")}>
          <div className={styles.senderBox}>
            {settings.senderId ? (
              <Tag disabled size="small">
                {settings.senderId}
              </Tag>
            ) : (
              <Text className={styles.senderEmpty}>{t("send.noSender")}</Text>
            )}
          </div>
        </Field>

        <div className={styles.fieldBlock}>
          <div className={styles.fieldHeader}>
            <Label>{t("send.to")}</Label>
            <Button
              size="small"
              appearance="subtle"
              icon={<Person20Regular />}
              onClick={() => setContactsOpen(true)}
            >
              {t("send.contacts")}
            </Button>
          </div>
          <RecipientsInput
            value={recipients}
            onChange={setRecipients}
            placeholder={t("send.recipientsPlaceholder")}
          />
          <Text className={styles.hint}>{t("send.recipientsHint")}</Text>
        </div>

        <div className={styles.fieldBlock}>
          <div className={styles.fieldHeader}>
            <Label>{t("send.message")}</Label>
            <Button
              size="small"
              appearance="subtle"
              icon={<DocumentText20Regular />}
              onClick={() => setTemplatesOpen(true)}
            >
              {t("send.template")}
            </Button>
          </div>
          <Textarea
            value={message}
            onChange={(_, d) => setMessage(d.value)}
            rows={4}
            resize="vertical"
          />
          <Text className={styles.counter}>
            {t("common.counter.exact", {
              count: seg.length,
              segments: seg.segments,
              remaining: seg.remaining,
            })}
          </Text>
        </div>

        {hasSignature && (
          <Switch
            checked={appendSignature}
            onChange={(_, d) => setAppendSignature(d.checked)}
            label={t("send.appendSignature")}
          />
        )}

        <Switch
          checked={scheduled}
          onChange={(_, d) => setScheduled(d.checked)}
          label={t("send.schedule")}
        />
        {scheduled && (
          <Field
            label={t("send.deliveryTime")}
            hint={t("send.deliveryTimeHint", { offset: localTimezoneLabel() })}
          >
            <Input
              type="datetime-local"
              value={scheduleAt}
              onChange={(_, d) => setScheduleAt(d.value)}
            />
          </Field>
        )}
        <ScrollFade visible={showFade} onClick={scrollDown} />
      </div>

      <div className={styles.footer}>
        <div className={styles.summary}>
          <span>
            {t("send.balance", {
              value: balance ? `${balance.amount} ${balance.currency}`.trim() : t("send.none"),
            })}
          </span>
          <span>
            {t("send.estCost", {
              value: estLoading
                ? "…"
                : estCost != null
                  ? t("send.estimatePrefix", {
                      value: `${estCost.toFixed(3)} ${balance?.currency ?? ""}`.trim(),
                    })
                  : t("send.none"),
            })}
          </span>
        </div>
        <Button
          className={styles.sendButton}
          appearance="primary"
          onClick={onSend}
          disabled={sending}
        >
          {sending
            ? t("send.action.sending")
            : scheduled
              ? t("send.action.schedule")
              : t("send.action.send")}
        </Button>
      </div>

      <TemplatesDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        onInsert={insertTemplate}
        mailMode={mailMode}
      />
      <ContactsPickerDialog
        open={contactsOpen}
        graphToken={graphToken}
        onOpenChange={setContactsOpen}
        onSelect={(phone) =>
          setRecipients((prev) => (prev.includes(phone) ? prev : [...prev, phone]))
        }
      />
    </div>
  );
};

export default SendSmsPanel;
